/**
 * Session équipe — utilisée par les surfaces `/ops` (et plus tard `/admin`).
 *
 * Le jeton d'accès est court et vit en `sessionStorage` : il disparaît quand
 * l'onglet se ferme. Une tablette d'entrée reste allumée toute la nuit, alors
 * le jeton de rafraîchissement permet de tenir la soirée sans redemander le
 * mot de passe à quelqu'un qui a les mains prises.
 *
 * Le front ne décide jamais de ce qu'un membre de l'équipe a le droit de faire :
 * `permissions` sert uniquement à masquer une action impossible. Chaque
 * endpoint revérifie.
 */

import { apiClient, ApiError } from './apiClient'

const STORAGE_KEY = 'leyamo.staff.session'

export interface StaffUser {
  id: string
  email: string
  full_name: string
  /** La langue de l'interface, enregistrée sur le compte. */
  locale: string
  /** Mot de passe provisoire encore en place : « Mon compte » s'impose. */
  must_change_password?: boolean
}

export interface StaffSession {
  access_token: string
  refresh_token: string
  expires_at: number
  user: StaffUser
}

interface SessionResponse {
  access_token: string
  refresh_token: string
  expires_in: number
  user: StaffUser
}

export interface MeResponse {
  user: StaffUser
  roles: { id: string; code: string; event_id: string | null }[]
  permissions: string[]
  event_id: string | null
}

function persist(response: SessionResponse): StaffSession {
  const session: StaffSession = {
    access_token: response.access_token,
    refresh_token: response.refresh_token,
    // Marge de 30 s : on renouvelle avant l'expiration, pas après un échec.
    expires_at: Date.now() + (response.expires_in - 30) * 1000,
    user: response.user,
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  return session
}

export function readSession(): StaffSession | null {
  const raw = sessionStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as StaffSession
  } catch {
    sessionStorage.removeItem(STORAGE_KEY)
    return null
  }
}

/**
 * Le mot de passe provisoire vient d'être remplacé : la session le retient,
 * pour que l'écran d'équipe s'ouvre sans redemander le changement.
 */
export function markPasswordChanged(): StaffSession | null {
  const session = readSession()
  if (!session) return null
  const updated = { ...session, user: { ...session.user, must_change_password: false } }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
  return updated
}

export async function changeMyPassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await apiClient.put('/auth/me/password', {
    current_password: currentPassword,
    new_password: newPassword,
  }, { token: await accessToken() })
}

export function clearSession(): void {
  sessionStorage.removeItem(STORAGE_KEY)
}

export async function login(email: string, password: string): Promise<StaffSession> {
  return persist(await apiClient.post<SessionResponse>('/auth/staff/login', { email, password }))
}

export async function logout(): Promise<void> {
  const session = readSession()
  clearSession()
  if (!session) return
  try {
    await apiClient.post('/auth/logout', { refresh_token: session.refresh_token })
  } catch {
    // Fermer la session localement suffit ; le serveur expirera la famille.
  }
}

/**
 * Le renouvellement en cours, partagé par tous les appels.
 *
 * Un écran qui charge six choses à la fois, une fois le jeton expiré,
 * envoyait six renouvellements avec le MÊME jeton de rafraîchissement. Le
 * serveur voit le deuxième comme un rejeu — exactement le signe d'un vol —
 * et révoque toute la famille de sessions : déconnexion au hasard, en pleine
 * soirée. Un seul renouvellement part ; les autres l'attendent.
 */
let refreshing: Promise<StaffSession> | null = null

/** Renvoie un jeton d'accès valide, en le renouvelant si nécessaire. */
export async function accessToken(): Promise<string> {
  const session = readSession()
  if (!session) throw new ApiError({ code: 'UNAUTHENTICATED', message: 'Session fermée.', status: 401 })
  if (Date.now() < session.expires_at) return session.access_token

  if (!refreshing) {
    refreshing = apiClient
      .post<SessionResponse>('/auth/refresh', { refresh_token: session.refresh_token })
      .then(persist)
      .finally(() => {
        refreshing = null
      })
  }
  return (await refreshing).access_token
}

export async function fetchMe(eventId?: string): Promise<MeResponse> {
  return apiClient.get<MeResponse>('/auth/me', {
    token: await accessToken(),
    ...(eventId ? { eventId } : {}),
  })
}
